import { z } from 'zod'

/**
 * Generated schemas are never `additionalProperties: false` at rest (a
 * confirmed hey-api zod-plugin gap), and success schemas are additionally
 * `z.union([RealShape, z.string()])` almost everywhere (the contract's
 * unconstrained legacy string alternative). This walks every wrapper Zod
 * produces (object/array/optional/nullable/union/intersection) and makes
 * every object encountered strict, however deeply nested.
 */
export const strictify = <Schema extends z.ZodType>(schema: Schema): Schema => {
  if (schema instanceof z.ZodObject) {
    const shape = Object.fromEntries(
      Object.entries(schema.def.shape).map(([key, value]) => [key, strictify(value as z.ZodType)])
    )
    return z.object(shape).strict() as unknown as Schema
  }
  if (schema instanceof z.ZodArray) {
    const Ctor = schema.constructor as new (def: z.core.$ZodArrayDef) => z.ZodArray
    return new Ctor({
      ...schema.def,
      element: strictify(schema.def.element as z.ZodType)
    }) as unknown as Schema
  }
  if (schema instanceof z.ZodOptional) {
    return strictify(schema.def.innerType as z.ZodType).optional() as unknown as Schema
  }
  if (schema instanceof z.ZodNullable) {
    return strictify(schema.def.innerType as z.ZodType).nullable() as unknown as Schema
  }
  if (schema instanceof z.ZodUnion) {
    const options = schema.options as z.ZodType[]
    return z.union(
      options.map((option) => strictify(option)) as [z.ZodType, z.ZodType, ...z.ZodType[]]
    ) as unknown as Schema
  }
  if (schema instanceof z.ZodIntersection) {
    const left = schema.def.left as z.ZodType
    const right = schema.def.right as z.ZodType
    // z.intersection(z.unknown(), X) drops X's own `.strict()` rejection of
    // unrecognized keys (a Zod v4 intersection-merge quirk) -- hey-api emits
    // this shape as a placeholder for an allOf/not composition it can't
    // otherwise express, so unwrapping to the real side is exactly equivalent.
    if (left instanceof z.ZodUnknown) return strictify(right) as unknown as Schema
    if (right instanceof z.ZodUnknown) return strictify(left) as unknown as Schema
    return z.intersection(strictify(left), strictify(right)) as unknown as Schema
  }
  return schema
}
